import {
  accessoryName,
  formatAddress,
  formatMoney,
  gafScorecard,
  paceWords,
  tamkoScorecard,
  tierLabel,
  toCents,
  type JobFileEntry,
  type Scorecard,
  type ScoreBar,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { fmtDateOnly } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { signJobFile } from "../../lib/queries";
import { type WarrantyJob, type WarrantyRow } from "../../lib/warranties";
import { fetchWarrantyBoard } from "../../lib/warrantyBoard";

/**
 * The Warranties page (ADR 0309).
 *
 * Top: the two certifications, each a card of progress bars against its
 * programme year with an on-track badge. Then the work: completed jobs whose
 * warranty is still to be registered, and open material orders a part short
 * of the warranty they could carry. Under it, every warranty registered, with
 * its receipt.
 *
 * All the arithmetic is `warranties.ts`'s in core; this lays it out.
 */
export function WarrantiesReport() {
  const board = useAsync(fetchWarrantyBoard, []);
  const [manufacturer, setManufacturer] = useState<"" | "TAMKO" | "GAF">("");
  const [year, setYear] = useState("");
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  const data = board.data;
  const tamko = useMemo(
    () => (data ? tamkoScorecard(data.warranties, data.today) : null),
    [data],
  );
  const gaf = useMemo(
    () =>
      data
        ? gafScorecard(data.warranties, data.rewardsSquares, data.today)
        : null,
    [data],
  );

  const years = useMemo(() => {
    const set = new Set<string>();
    for (const row of data?.warranties ?? [])
      if (row.registered_on) set.add(row.registered_on.slice(0, 4));
    return [...set].sort().reverse();
  }, [data]);

  const rows = useMemo(
    () =>
      (data?.warranties ?? []).filter(
        (row) =>
          (!manufacturer || row.manufacturer === manufacturer) &&
          (!year || (row.registered_on ?? "").startsWith(year)),
      ),
    [data, manufacturer, year],
  );

  if (board.loading && data == null)
    return <div className="stub">Reading the warranties…</div>;
  if (board.error || !data) {
    return (
      <div className="block">
        <h2>Warranties</h2>
        <ErrorNote error={String(board.error ?? "Nothing came back.")} />
      </div>
    );
  }

  async function openReceipt(row: WarrantyRow) {
    const file = data?.receipts.get(row.id);
    if (!file) return;
    setOpening(row.id);
    setOpenError(null);
    try {
      window.open(await signJobFile(file), "_blank", "noreferrer");
    } catch (e) {
      setOpenError(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(null);
    }
  }

  return (
    <>
      <div className="scorecards">
        {tamko && <ScorecardView card={tamko} />}
        {gaf && <ScorecardView card={gaf} />}
      </div>

      <div className="block" style={{ marginBottom: 16 }}>
        <h2>Ready to register</h2>
        <div className="na-hint" style={{ marginBottom: 10 }}>
          The work is complete and Register Warranty is still open. Open the job
          and tick the step to file it.
        </div>
        {data.ready.length === 0 ? (
          <div className="tnone">Nothing waiting.</div>
        ) : (
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Roof</th>
                  <th>Priced as</th>
                  <th>Work complete</th>
                </tr>
              </thead>
              <tbody>
                {data.ready.map((entry) => (
                  <tr key={entry.job.id}>
                    <td>
                      <JobLink job={entry.job} />
                    </td>
                    <td>{entry.job.contact?.full_name ?? "—"}</td>
                    <td>{addressOf(entry.job)}</td>
                    <td>{entry.manufacturer ?? "—"}</td>
                    <td>{tierLabel(entry.tier)}</td>
                    <td>{fmtDateOnly(entry.completedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="block" style={{ marginBottom: 16 }}>
        <h2>Check the parts before ordering</h2>
        <div className="na-hint" style={{ marginBottom: 10 }}>
          Material orders not yet bought that are a part or two short of the
          warranty they could carry. The order screen says what the swap costs.
        </div>
        {data.alerts.length === 0 ? (
          <div className="tnone">Every open order has its parts.</div>
        ) : (
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer</th>
                  <th>Order</th>
                  <th>Has</th>
                  <th>Qualifies for</th>
                  <th>To reach</th>
                </tr>
              </thead>
              <tbody>
                {data.alerts.map((alert) => (
                  <tr key={alert.orderId}>
                    <td>
                      <JobLink job={alert.job} />
                    </td>
                    <td>{alert.job.contact?.full_name ?? "—"}</td>
                    <td>MO-{alert.orderNumber ?? "?"}</td>
                    <td>
                      {alert.advice.present.length} of{" "}
                      {alert.advice.present.length +
                        alert.advice.missing.length}{" "}
                      {alert.advice.manufacturer}
                    </td>
                    <td>
                      {alert.advice.tier
                        ? tierLabel(alert.advice.tier)
                        : "No enhanced warranty"}
                    </td>
                    <td>
                      <b>{tierLabel(alert.advice.target)}</b>:{" "}
                      {alert.advice.toTarget
                        .map((part) =>
                          part.replaces
                            ? `swap ${part.replaces.name} for ${part.name}`
                            : `add ${part.name}`,
                        )
                        .join("; ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="block">
        <h2>Registered warranties</h2>
        <ErrorNote error={openError} />
        <div className="viewswitch" role="group" aria-label="Filter warranties">
          {(["", "TAMKO", "GAF"] as const).map((value) => (
            <button
              key={value || "all"}
              type="button"
              className={manufacturer === value ? "on" : ""}
              aria-pressed={manufacturer === value}
              onClick={() => setManufacturer(value)}
            >
              {value || "All"}
            </button>
          ))}
          <select
            aria-label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            <option value="">Every year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        {rows.length === 0 ? (
          <div className="tnone">No warranties registered yet.</div>
        ) : (
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Roof</th>
                  <th>Warranty</th>
                  <th>Warranty #</th>
                  <th className="num">Squares</th>
                  <th className="num">Fee</th>
                  <th>Registered</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <WarrantyRowView
                    key={row.id}
                    row={row}
                    receipt={data.receipts.get(row.id) ?? null}
                    opening={opening === row.id}
                    onOpen={() => void openReceipt(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/** One registered, pending or ineligible warranty in the table. */
function WarrantyRowView({
  row,
  receipt,
  opening,
  onOpen,
}: {
  row: WarrantyRow;
  receipt: JobFileEntry | null;
  opening: boolean;
  onOpen: () => void;
}) {
  const grey = row.status !== "registered";
  return (
    <tr style={grey ? { opacity: 0.6 } : undefined}>
      <td>{row.job ? <JobLink job={row.job} /> : "—"}</td>
      <td>{row.job?.contact?.full_name ?? "—"}</td>
      <td>{row.job ? addressOf(row.job) : "—"}</td>
      <td>{row.manufacturer ?? "—"}</td>
      <td>
        <TierCell row={row} />
      </td>
      <td>{row.warranty_number ?? "—"}</td>
      <td className="num">{row.squares == null ? "—" : String(row.squares)}</td>
      <td className="num">
        {row.status === "registered" ? formatMoney(toCents(row.fee)) : "—"}
      </td>
      <td>{fmtDateOnly(row.registered_on) || "—"}</td>
      <td>
        {receipt ? (
          <button className="btn-small" disabled={opening} onClick={onOpen}>
            {opening ? "Opening…" : "Open"}
          </button>
        ) : (
          <span className="na-hint">none</span>
        )}
      </td>
    </tr>
  );
}

/** Which warranty, or why there is none. */
function TierCell({ row }: { row: WarrantyRow }) {
  if (row.status === "ineligible")
    return (
      <>
        <span className="badge draft">N/A</span> {row.ineligible_reason}
      </>
    );
  if (row.status === "pending")
    return (
      <>
        <span className="badge submitted">Not yet</span> {tierLabel(row.tier)}
      </>
    );
  return (
    <>
      {tierLabel(row.tier)}
      {row.accessories.length > 0 && (
        <div className="na-hint">
          {row.accessories
            .map((key) => accessoryName(row.manufacturer, key))
            .join(", ")}
        </div>
      )}
    </>
  );
}

/** The job's own address, else its contact's (ADR 0003). */
const addressOf = (job: WarrantyJob): string =>
  formatAddress(job.address ? job : (job.contact ?? {}));

/** A job number that opens the job in its own tab, like the boards do. */
function JobLink({ job }: { job: WarrantyJob }) {
  return (
    <a href={`#job=${job.id}`} target="_blank" rel="noreferrer">
      #{job.job_number}
    </a>
  );
}

function ScorecardView({ card }: { card: Scorecard }) {
  return (
    <div className="block scorecard">
      <div className="scorecard-head">
        <h2>{card.name}</h2>
        <span className={`badge ${card.onTrack ? "approved" : "warn"}`}>
          {paceWords(card.onTrack)}
        </span>
      </div>
      <div className="na-hint" style={{ marginBottom: 10 }}>
        Programme year {fmtDateOnly(card.year.from)} to{" "}
        {fmtDateOnly(card.year.to)}, {Math.round(card.year.elapsed * 100)}%
        gone.
      </div>
      {card.bars.map((bar) => (
        <ScoreBarView key={bar.key} bar={bar} />
      ))}
    </div>
  );
}

function ScoreBarView({ bar }: { bar: ScoreBar }) {
  const { pace } = bar;
  const width = Math.min(100, (pace.done / pace.target) * 100);
  const marker = Math.min(100, (pace.expectedByNow / pace.target) * 100);
  const unit = bar.unit === "squares" ? "SQ" : "";
  return (
    <div className="scorebar" data-testid={`scorebar-${bar.key}`}>
      <div className="scorebar-label">
        <span>{bar.label}</span>
        <b>
          {pace.done}
          {unit && ` ${unit}`} / {pace.target}
          {unit && ` ${unit}`}
        </b>
      </div>
      <div
        className="scorebar-track"
        role="progressbar"
        aria-label={bar.label}
        aria-valuemin={0}
        aria-valuemax={pace.target}
        aria-valuenow={pace.done}
      >
        <div
          className={`scorebar-fill${pace.onTrack ? "" : " behind"}`}
          style={{ width: `${width}%` }}
        />
        <div className="scorebar-marker" style={{ left: `${marker}%` }} />
      </div>
      <div className="na-hint">
        {paceWords(pace.onTrack)}: the year&apos;s pace wants{" "}
        {Math.round(pace.expectedByNow)} by today, {pace.remaining} to go
        {pace.projected !== null && `, on course for ${pace.projected}`}.
        {bar.note && ` ${bar.note}`}
      </div>
    </div>
  );
}
